import React, {
  FC,
  FormEvent,
  PropsWithChildren,
  ReactNode,
} from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  CONTACTS_VALUES,
  routes,
} from 'appConstants';
import {
  ButtonV4,
  Link,
} from 'components';
import {
  _t,
  openApplicationForm,
  phoneRefactor,
} from 'utils';
import { SEE_ALSO } from 'containers/Resolutions/shared';

import banner from 'assets/img/main-v4/hero-curve.png';
import iconSearch from 'assets/img/header-v4/icon-search.svg';
import iconArrow from 'assets/img/resolutions-v4/icon-arrow.svg';
import iconSend from 'assets/img/resolutions-v4/icon-send.svg';
import iconPhone from 'assets/img/news-v4/icon-phone.svg';
import iconMail from 'assets/img/news-v4/icon-mail.svg';

import { MEDIA_TABS, isMediaTabActive, MediaTabId } from '../mediaTabs';
import styles from '../MediaNews/styles.module.scss';

type Props = {
  title: string,
  crumbCurrent: string,
  toolbarExtra?: ReactNode,
  searchValue?: string,
  searchPlaceholder?: string,
  onSearchChange?: (value: string) => void,
  onSearchSubmit?: (event: FormEvent) => void,
  showSearch?: boolean,
};

const MediaShell: FC<PropsWithChildren<Props>> = ({
  title,
  crumbCurrent,
  toolbarExtra,
  searchValue = '',
  searchPlaceholder = 'Поиск ...',
  onSearchChange,
  onSearchSubmit,
  showSearch = false,
  children,
}) => {
  const location = useLocation();
  const smiPhone = CONTACTS_VALUES.phones.main.smi.split(',');
  const smiPhoneMain = phoneRefactor(smiPhone[0]);
  const smiPhoneExt = smiPhone[1] || '';
  const smiEmail = CONTACTS_VALUES.emails.smi.value;

  const isTabActive = (tabId: MediaTabId) => () => (
    isMediaTabActive(tabId, location.pathname, location.search)
  );

  return (
    <div className={styles.page}>
      <div className={styles.banner} aria-hidden>
        <img src={banner} alt="" />
      </div>

      <section className={styles.head}>
        <div className={styles.headRow}>
          <h1 className={styles.title}>{_t(title)}</h1>
          {showSearch ? (
            <form className={styles.search} onSubmit={onSearchSubmit}>
              <input
                className={styles.searchInput}
                type="search"
                value={searchValue}
                onChange={(event) => onSearchChange?.(event.target.value)}
                placeholder={_t(searchPlaceholder)}
                aria-label={_t(searchPlaceholder)}
              />
              <button type="submit" className={styles.searchBtn} aria-label={_t('Найти')}>
                <img src={iconSearch} alt="" width={14} height={14} />
              </button>
            </form>
          ) : null}
        </div>
        <nav className={styles.crumbs} aria-label={_t('Навигация')}>
          <Link to={routes.main.root} className={styles.crumbLink}>{_t('Главная')}</Link>
          <span className={styles.crumbLine} aria-hidden />
          <Link to={routes.media.news.root} className={styles.crumbLink}>
            {_t('Новости и медиа')}
          </Link>
          <span className={styles.crumbLine} aria-hidden />
          <span className={styles.crumbCurrent}>{_t(crumbCurrent)}</span>
        </nav>
      </section>

      <div className={styles.toolbar}>
        <ul className={styles.tabs}>
          {MEDIA_TABS.map((tab) => (
            <li key={tab.id}>
              <NavLink
                to={tab.to}
                exact={tab.id === 'all' || tab.id === 'news'}
                className={styles.tab}
                activeClassName={styles.tabActive}
                isActive={isTabActive(tab.id)}
              >
                {_t(tab.label)}
              </NavLink>
            </li>
          ))}
        </ul>
        {toolbarExtra}
      </div>

      {children}

      <section className={styles.smiBand}>
        <div className={styles.smiInfo}>
          <h2 className={styles.smiTitle}>{_t('Контактная информация для СМИ')}</h2>
          <div className={styles.smiContacts}>
            <a
              href={`tel:${CONTACTS_VALUES.phones.main.value}`}
              className={styles.smiLink}
            >
              <img src={iconPhone} alt="" width={18} height={18} />
              <span>
                {smiPhoneMain}
                {smiPhoneExt ? ` ${_t('доб.')} (${smiPhoneExt})` : ''}
              </span>
            </a>
            <a
              href={`mailto:${smiEmail}`}
              className={styles.smiLink}
            >
              <img src={iconMail} alt="" width={18} height={18} />
              <span>{CONTACTS_VALUES.emails.smi.text}</span>
            </a>
          </div>
        </div>
        <ButtonV4
          type="button"
          label="Отправить запрос"
          variant="outline"
          tone="onLight"
          className={styles.smiBtn}
          iconSrc={iconSend}
          iconSize={16}
          onClick={() => openApplicationForm({
            type: 'media',
            title: 'Запрос для СМИ',
            path: location.pathname,
          })}
        />
      </section>

      <section className={styles.seeAlso}>
        <h2 className={styles.seeTitle}>{_t('Смотрите также')}</h2>
        <div className={styles.seeGrid}>
          {SEE_ALSO.map((item) => (
            <div
              key={item.id}
              className={styles.seeCard}
              style={{ backgroundImage: `url(${item.image})` }}
            >
              <div className={styles.seeCardTitle}>{_t(item.title)}</div>
              <ButtonV4
                to={item.to}
                label="Перейти в раздел"
                variant="solid"
                tone="onLight"
                iconSrc={iconArrow}
              />
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default MediaShell;
